import {lazy, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {useDecision} from '@s-ui/pde'
import {atomButtonColors} from '@s-ui/react-atom-button'
import {atomIconColors} from '@s-ui/react-atom-icon'
import {atomToastAutoCloseTimes, atomToastPositions} from '@s-ui/react-atom-toast'
import Context from '@s-ui/react-context'
import useMount from '@s-ui/react-hooks/lib/useMount/index.js'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoFreespeeTrack, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'

import {COLORS, DESIGNS, DESINGS_ICON, SIZES, TARGETS, TRACKS, TYPES} from './config.js'
import LeadPhoneCallButton from './lead-phone-call-button.js'
import LeadPhoneCallLink from './lead-phone-call-link.js'
import {formatPhone, getAdobeTrackingParams, isNumber, mapTrackData} from './utils.js'

const AtomToast = lazy(() => import(/* webpackChunkName: "AtomToast" */ '@s-ui/react-atom-toast'))

const ELEMENTS_BY = {
  [TYPES.button]: LeadPhoneCallButton,
  [TYPES.link]: LeadPhoneCallLink
}
const NO_OP = () => {}

function LeadPhoneCall({
  adId,
  authenticationHitInformation2,
  color,
  contractId,
  design = DESIGNS.SOLID,
  designIcon = DESINGS_ICON.SOLID,
  extendedLeadTrackingData = {},
  hasCriteoEvents = false,
  isAlreadyContacted = false,
  isFullWidth,
  isProfessionalDealer,
  multiprovince = '',
  negative = false,
  onGetPhoneError = NO_OP,
  onLeadPhoneCall = NO_OP,
  onPhoneRevealed = NO_OP,
  phone,
  phoneViewedHitOrigin,
  revealedPhone,
  showIcon,
  size = SIZES.medium,
  target = TARGETS.vehicle,
  trackHitOrigin = false,
  text: buttonText,
  type = TYPES.button,
  underlineRevealedPhone = false,
  onBeforeClick = null
}) {
  const {isLogged} = useContext(AuthenticationContext)
  const {triggerLoginModal, isLoginModalOpen} = useContext(LoginModalContext)

  const [text, setText] = useState(buttonText)
  const [phoneTrackingSessionId, setPhoneTrackingSessionId] = useState()
  const [adobeTrackingParams, setAdobeTrackingParams] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [showGetPhoneError, setShowGetPhoneError] = useState(false)

  const {track: analyticsTrack} = useAnalyticsTrack()
  const {track: criteoTrack} = useCriteoTrack()
  const {track: criteoFreespeeTrack} = useCriteoFreespeeTrack()
  const {domain, i18n} = useContext(Context)
  const {enabled: isTrackingPhoneHitIdEnabled} = useDecision('ff_web_buyer_tracking_phone_hit_id')
  const delayToNextFrame = useDelayToNextFrame()

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const {LEAD_HIT_TYPES} = config.get('user')
  const {PRIVATE_CONTRACT_ID} = config.get('dealer')
  const CALL_TRACKING_SERVICES = config.get('CALL_TRACKING_SERVICES')
  const {DEFAULT_CALL_TRACKING_SERVICE} = config.get('tracking')

  // Ad type
  const isPrivate = Number(contractId) === PRIVATE_CONTRACT_ID || isProfessionalDealer === false

  const isDealer = isProfessionalDealer || !isPrivate
  // Target (used for tagging)
  const isTargetDealer = target === TARGETS.dealer
  const isTargetDealerConfiguratorSuccess = target === TARGETS.dealerConfiguratorSuccess
  const isTargetDealerConfiguratorPopup = target === TARGETS.dealerConfiguratorPopup
  const isTargetDealerHeader = target === TARGETS.dealerHeader
  const isTargetDealerInformation = target === TARGETS.dealerInformation
  const isTargetDealerSearch = target === TARGETS.dealerSearch
  const isTargetDealerProvinceSearch = target === TARGETS.dealerProvinceSearch
  const isTargetFavoriteDealer = target === TARGETS.favoriteDealer

  const isDealerPhoneCall =
    isTargetDealer ||
    isTargetDealerConfiguratorSuccess ||
    isTargetDealerConfiguratorPopup ||
    isTargetDealerHeader ||
    isTargetDealerInformation ||
    isTargetDealerSearch ||
    isTargetDealerProvinceSearch ||
    isTargetFavoriteDealer

  const contextParams = isDealerPhoneCall ? {contractId} : {adId}

  // Component
  const Element = ELEMENTS_BY[type]
  const isPhoneRevealed = underlineRevealedPhone && Boolean(text) && isNumber(text)

  const _makePhoneCall = async ({phoneNumber, hitId: existingHitId, phoneTrackingSessionId} = {}) => {
    setIsLoading(false)
    if (!phoneNumber) return
    window.location.href = `tel:${phoneNumber}`
    hasCriteoEvents &&
      criteoTrack({
        event: criteoTrackEvents.TRACK_TRANSACTION,
        eventData: {adId}
      })

    const leadType = isDealerPhoneCall ? LEAD_HIT_TYPES.CALL_DEALER : LEAD_HIT_TYPES.CALL_AD

    // Motos Desktop should not be saving any `/call` stats hits
    if (!isMobile && DEFAULT_CALL_TRACKING_SERVICE === CALL_TRACKING_SERVICES.DIALOGA) {
      _makeLeadTrack({phoneNumber})
    } else if (isTrackingPhoneHitIdEnabled && existingHitId) {
      _makeLeadTrack({phoneNumber, hitId: existingHitId, phoneTrackingSessionId})
    } else {
      _saveStatsHit({leadType, trackAction: ({hitId}) => _makeLeadTrack({phoneNumber, hitId, phoneTrackingSessionId})})
    }
    onLeadPhoneCall()
  }

  const _saveStatsHit = async ({leadType, trackAction}) => {
    const [error, response] = await domain.get('save_lead_hit_user_use_case').execute({contractId, adId, leadType})

    if (error) return

    const {hitId} = response
    trackAction({hitId})
  }

  const _trackMappedData = async ({trackingData, hitOrigin}) => {
    const {products} = await domain.get('get_advertisements_tracking_use_case').execute({
      ads: [adId],
      context: 'other'
    })
    const trackingDataWithProducts = mapTrackData({
      extendedLeadTrackingData,
      trackingData,
      products: extendedLeadTrackingData.products || products,
      hitOrigin
    })

    analyticsTrack(trackingDataWithProducts)
  }

  const makeTrack = ({trackingDataByTarget, hitId, phoneTrackingSessionId, hitOrigin: customHitOrigin}) => {
    const hitOrigin = trackHitOrigin ? trackingDataByTarget?.data?.hit_origin : customHitOrigin
    const trackingData = {
      ...trackingDataByTarget,
      data: {
        ...trackingDataByTarget.data,
        hit_id: hitId,
        ...(phoneTrackingSessionId && {phone_tracking_session_id: phoneTrackingSessionId})
      }
    }
    _trackMappedData({trackingData, hitOrigin})
  }

  const _makeLeadTrack = ({hitId, phoneNumber, phoneTrackingSessionId} = {}) => {
    const {PHONE_CALLED} = TRACKS[target]
    const trackingDataByTarget = {
      ...PHONE_CALLED,
      data: {...PHONE_CALLED.data, ...(isDealer && {hit_information2: phoneNumber})}
    }
    makeTrack({trackingDataByTarget, hitId, phoneTrackingSessionId})
  }

  const _makeViewPhonetrack = ({hitId, phoneTrackingSessionId} = {}) => {
    const trackingDataByTarget = TRACKS[target].PHONE_VIEW
    const isPhoneViewedEvent = trackingDataByTarget.event === 'Phone Viewed'
    makeTrack({
      trackingDataByTarget,
      hitId,
      phoneTrackingSessionId: isPhoneViewedEvent ? phoneTrackingSessionId : undefined,
      hitOrigin: isPhoneViewedEvent ? phoneViewedHitOrigin : undefined
    })
  }

  const _setTrackingPhoneNumber = async ({hitId} = {}) => {
    const [error, response = {}] = await domain.get('get_tracking_phone_dealer_use_case').execute({
      ...contextParams,
      ...adobeTrackingParams,
      ...(isTrackingPhoneHitIdEnabled && hitId && {adobeMarketingCloudVisitorId: hitId}),
      multiprovince
    })

    if (error || !response.phoneNumber) {
      if (isDealerPhoneCall) {
        setIsLoading(false)
        return {phoneNumber: phone, sessionId: ''}
      }
      try {
        const {phone} = await getAdContactPhone()
        setIsLoading(false)
        return {phoneNumber: phone}
      } catch (e) {
        return setIsLoading(false)
      }
    }

    const {phoneNumber, sessionId, trackingId} = response

    return {phoneNumber: phoneNumber || phone, sessionId, trackingId}
  }

  const getAdContactPhone = async () => {
    const [error, response] = await domain.get('get_ad_contact_phone_ads_use_case').execute({adId, isPrivate})

    if (error) {
      setShowGetPhoneError(true)
      setIsLoading(false)
      return onGetPhoneError({error})
    }
    return {phone: response.phone}
  }

  const _handleCall = async () => {
    if (isPrivate || (!isDealerPhoneCall && DEFAULT_CALL_TRACKING_SERVICE === CALL_TRACKING_SERVICES.DIALOGA)) {
      try {
        const {phone} = await getAdContactPhone()
        _makePhoneCall({phoneNumber: phone})
        return setIsLoading(false)
      } catch (e) {
        return setIsLoading(false)
      }
    }

    if (isNumber(text)) {
      _makePhoneCall({phoneNumber: text, phoneTrackingSessionId})
    } else if (isTrackingPhoneHitIdEnabled) {
      const leadType = isDealerPhoneCall ? LEAD_HIT_TYPES.CALL_DEALER : LEAD_HIT_TYPES.CALL_AD
      const [, {hitId} = {}] = await domain.get('save_lead_hit_user_use_case').execute({contractId, adId, leadType})
      _setTrackingPhoneNumber({hitId})
        .then(({phoneNumber, trackingId}) => _makePhoneCall({phoneNumber, hitId, phoneTrackingSessionId: trackingId}))
        .catch(() => setIsLoading(false))
    } else {
      _setTrackingPhoneNumber()
        .then(({phoneNumber, trackingId}) => _makePhoneCall({phoneNumber, phoneTrackingSessionId: trackingId}))
        .catch(() => setIsLoading(false))
    }
  }

  const showPhone = (phoneNumber, trackingId) => {
    const formattedPhone = formatPhone(phoneNumber)

    setText(formattedPhone)
    onPhoneRevealed({phone: formattedPhone, phoneTrackingSessionId: trackingId})
  }

  const _handleShowPhone = async () => {
    if (isDealerPhoneCall && DEFAULT_CALL_TRACKING_SERVICE === CALL_TRACKING_SERVICES.DIALOGA) {
      showPhone(phone)
      return setIsLoading(false)
    }

    if (isPrivate || DEFAULT_CALL_TRACKING_SERVICE === CALL_TRACKING_SERVICES.DIALOGA) {
      try {
        const {phone} = await getAdContactPhone()
        showPhone(phone)
        _saveStatsHit({
          leadType: LEAD_HIT_TYPES.VIEW_AD_PHONE,
          trackAction: _makeViewPhonetrack
        })
        return setIsLoading(false)
      } catch (e) {
        return setIsLoading(false)
      }
    }

    if (isTrackingPhoneHitIdEnabled) {
      const [, {hitId} = {}] = await domain
        .get('save_lead_hit_user_use_case')
        .execute({contractId, adId, leadType: LEAD_HIT_TYPES.VIEW_DEALER_PHONE})
      _setTrackingPhoneNumber({hitId})
        .then(({phoneNumber, sessionId, trackingId}) => {
          hasCriteoEvents && sessionId && criteoFreespeeTrack({sessionId})
          showPhone(phoneNumber, trackingId)
          setPhoneTrackingSessionId(trackingId)
          setIsLoading(false)
          _makeViewPhonetrack({hitId, phoneTrackingSessionId: trackingId})
        })
        .catch(() => setIsLoading(false))
    } else {
      _setTrackingPhoneNumber()
        .then(({phoneNumber, sessionId, trackingId}) => {
          hasCriteoEvents && sessionId && criteoFreespeeTrack({sessionId})
          showPhone(phoneNumber, trackingId)
          setPhoneTrackingSessionId(trackingId)
          setIsLoading(false)

          _saveStatsHit({
            leadType: LEAD_HIT_TYPES.VIEW_DEALER_PHONE,
            trackAction: ({hitId}) => _makeViewPhonetrack({hitId, phoneTrackingSessionId: trackingId})
          })
        })
        .catch(() => setIsLoading(false))
    }
  }

  const _handleClick = e => {
    e.preventDefault()
    e.stopPropagation()

    const proceedWithClick = () => {
      setIsLoading(true)
      delayToNextFrame(() => {
        const shouldCall = isMobile || isNumber(text)
        shouldCall ? _handleCall() : _handleShowPhone()
      })
    }

    const continueWithClick = () => {
      if (isPrivate && !isLogged && !isLoginModalOpen) {
        const {LOGIN_SOURCE_ID_LEAD} = config.get('login')
        const {HIT_ORIGINS} = config.get('tracking')

        triggerLoginModal({
          modalProps: {
            source: LOGIN_SOURCE_ID_LEAD,
            trackingOrigin: HIT_ORIGINS.LEAD_EMAIL,
            hitInformation2: authenticationHitInformation2,
            onLogin: proceedWithClick,
            onClose: () => {}
          }
        })

        return
      }

      proceedWithClick()
    }

    if (onBeforeClick) {
      onBeforeClick(continueWithClick)
      return
    }

    continueWithClick()
  }

  useMount(() => {
    if (isAlreadyContacted) {
      const alreadyContactedText = i18n.t('COMMON.MODAL_RELATED_ADS_ALREADY_CONTACTED_BUTTON')
      return setText(alreadyContactedText)
    }

    const initialText = isMobile ? i18n.t('COMMON_PHONE_CALL') : i18n.t('COMMON_SHOW_PHONE')

    getAdobeTrackingParams().then(setAdobeTrackingParams)

    !buttonText && setText(revealedPhone?.phone || initialText)
  })

  // A sibling button of the same ad can reveal the phone after this one mounted.
  const sharedPhone = revealedPhone?.phone
  const sharedPhoneTrackingSessionId = revealedPhone?.phoneTrackingSessionId

  useEffect(() => {
    if (!sharedPhone || isAlreadyContacted) return
    setText(sharedPhone)
    setPhoneTrackingSessionId(sharedPhoneTrackingSessionId)
  }, [sharedPhone, sharedPhoneTrackingSessionId, isAlreadyContacted])

  return (
    <>
      <Element
        color={color}
        design={design}
        designIcon={designIcon}
        isAlreadyContacted={isAlreadyContacted}
        isFullWidth={isFullWidth}
        isLoading={isLoading}
        isPhoneRevealed={isPhoneRevealed}
        negative={negative}
        onClick={_handleClick}
        showIcon={showIcon}
        size={size}
        text={text}
      />
      {/* The boundary exists only while the toast shows. Rendered empty on the server, it was still a
          dehydrated boundary: any re-render of this component before React hydrated it (login state,
          flag decisions) switched it to client rendering (React #421), three per ad detail. */}
      {showGetPhoneError && (
        <Suspense fallback={null}>
          <AtomToast
            autoCloseTime={atomToastAutoCloseTimes.short}
            globalClose
            iconClose={<IconClose color={atomIconColors.primary} />}
            onClose={() => setShowGetPhoneError(false)}
            position={atomToastPositions.topRight}
          >
            <div className="mt-LeadPhoneCall-error">
              <p className="mt-LeadPhoneCall-errorText">{i18n.t('COMMON.SOMETHING_WENT_WRONG')}</p>
            </div>
          </AtomToast>
        </Suspense>
      )}
    </>
  )
}

LeadPhoneCall.displayName = 'LeadPhoneCall'

LeadPhoneCall.propTypes = {
  adId: PropTypes.number.isRequired,
  authenticationHitInformation2: PropTypes.string,
  color: PropTypes.oneOf(Object.values(atomButtonColors)),
  contractId: PropTypes.number.isRequired,
  design: PropTypes.oneOf(Object.values(DESIGNS)),
  designIcon: PropTypes.oneOf(Object.values(DESINGS_ICON)),
  extendedLeadTrackingData: PropTypes.object,
  hasCriteoEvents: PropTypes.bool,
  isAlreadyContacted: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isProfessionalDealer: PropTypes.bool,
  multiprovince: PropTypes.string,
  negative: PropTypes.bool,
  onGetPhoneError: PropTypes.func,
  onLeadPhoneCall: PropTypes.func,
  onPhoneRevealed: PropTypes.func,
  phone: PropTypes.string.isRequired,
  phoneViewedHitOrigin: PropTypes.string,
  revealedPhone: PropTypes.shape({
    phone: PropTypes.string,
    phoneTrackingSessionId: PropTypes.string
  }),
  showIcon: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(SIZES)),
  target: PropTypes.oneOf(Object.values(TARGETS)),
  // This prop is used to determine whether to track the hit origin when event is triggered.
  // If true, it will include the config hit origin in the tracking data.
  trackHitOrigin: PropTypes.bool,
  text: PropTypes.string,
  type: PropTypes.oneOf(Object.values(TYPES)),
  underlineRevealedPhone: PropTypes.bool,
  onBeforeClick: PropTypes.func
}

export {
  COLORS as leadPhoneCallColors,
  DESIGNS as leadPhoneCallDesigns,
  DESINGS_ICON as leadPhoneCallDesignIcon,
  SIZES as leadPhoneCallSizes,
  TARGETS as leadPhoneCallTarget,
  TYPES as leadPhoneCallType
}
export default LeadPhoneCall
